import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { SERVICES } from "@/config/services";

// Bloc unifié « Nos services » : 4 grandes cartes vedettes illustrées (priorités)
// + 2 cartes secondaires, placé juste sous le hero. Chaque carte décrit l'espace.
export default function ServicesBlock() {
  const { user } = useAuth();
  // Connecté → assistant ; déconnecté → inscription puis redirection vers l'assistant
  // (l'ancienne ancre /#assistant-ia ne pointait plus vers aucune section → clic sans effet)
  const aiTarget = user && user !== false ? "/mon-assistant-ia" : "/inscription?next=/mon-assistant-ia";

  // 4 services phares — grandes cartes avec image de fond
  const featured = [
    {
      to: "/bons-plans",
      label: "Bons plans",
      desc: "Réductions et avantages négociés chez vos commerçants locaux, toute l'année.",
      img: "/images/services/bg_bons_plans.jpg",
      isNew: false,
    },
    {
      to: "/demande-de-devis",
      label: "Demande de devis",
      desc: "Décrivez votre besoin : les pros près de chez vous vous répondent gratuitement.",
      img: "/images/services/bg_devis.jpg",
      isNew: false,
    },
    {
      to: "/entre-voisins",
      label: "Entre Voisins",
      desc: "L'entraide locale gratuite : coups de main, prêts et services entre habitants du 28.",
      img: "/images/services/bg_voisins.jpg",
      isNew: true,
    },
    {
      to: "/petites-annonces",
      label: "Petites annonces",
      desc: "Achetez, vendez et dénichez de bonnes affaires entre voisins près de chez vous.",
      img: "/images/services/bg_annonces.jpg",
      isNew: true,
    },
  ];

  // Les autres services — cartes secondaires compactes
  const secondary = [
    {
      to: "/carte-de-visite-digitale",
      label: "Carte de visite digitale",
      desc: "Créez et partagez votre carte en un geste, sans application.",
      img: "/images/services/bg_carte.jpg",
      isNew: true,
    },
    {
      to: aiTarget,
      label: "Assistant Agent IA",
      desc: "Posts, e-mails, visuels et documents générés en quelques secondes.",
      img: "/images/services/bg_ia.jpg",
      isNew: true,
    },
  ];

  return (
    <section className="relative overflow-hidden" data-testid="home-services-block">
      {/* Fond dégradé chaud + halos pour faire ressortir le bloc */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#FFF4EC] via-[#FDEDE4] to-[#FAF8F5]" aria-hidden />
      <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full bg-[#E05638]/10 blur-3xl" aria-hidden />
      <div className="absolute -bottom-28 -right-20 w-96 h-96 rounded-full bg-[#172A3A]/10 blur-3xl" aria-hidden />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
        <div className="mb-6 md:mb-8">
          <div className="text-[11px] font-bold uppercase tracking-widest text-[#E05638]">100 % local &amp; gratuit</div>
          <h2 className="mt-1 font-heading text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900">
            Nos services
          </h2>
          <p className="mt-1.5 text-sm md:text-base text-slate-600">
            Tout PROXIreduc-28 à portée de clic : vos bons plans, vos devis, l'entraide et les annonces près de chez vous.
          </p>
        </div>

        {/* 1 · Cartes vedettes illustrées — logo officiel COMPLET posé directement (sans fond rond) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {featured.map(({ to, label, desc, img, isNew }) => {
            const brandImg = SERVICES[to.replace("/", "")]?.uiLogo;
            return (
            <Link
              key={to}
              to={to}
              data-testid={`service-card-${to}`}
              className="group relative flex flex-col justify-end overflow-hidden rounded-2xl min-h-[230px] md:min-h-[300px] p-5 text-white shadow-[0_14px_34px_-18px_rgba(23,42,58,0.55)] transition-all hover:-translate-y-1"
            >
              {/* Image de fond */}
              <img
                src={img}
                alt={label}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              {/* Dégradé pour la lisibilité du texte */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/5" aria-hidden />
              {/* Logo officiel ENTIER posé directement sur l'image (sans fond rond), légèrement agrandi */}
              {brandImg && (
                <img
                  src={brandImg}
                  alt={`Logo ${label}`}
                  loading="lazy"
                  data-testid={`service-badge-${to}`}
                  className="absolute top-3 left-3 w-20 h-20 md:w-24 md:h-24 object-contain drop-shadow-[0_4px_12px_rgba(0,0,0,0.55)]"
                />
              )}
              {isNew && (
                <span className="absolute top-3 right-3 text-[9px] font-extrabold uppercase tracking-wider text-white bg-[#E05638] rounded-full px-1.5 py-0.5 leading-none shadow">
                  Nouveau
                </span>
              )}
              <div className="relative">
                <h3 className="font-heading font-extrabold text-lg md:text-xl leading-tight drop-shadow">
                  {label}
                </h3>
                <p className="mt-1.5 text-xs md:text-sm text-white/85 leading-snug">
                  {desc}
                </p>
                <span className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-white group-hover:gap-2 transition-all">
                  Découvrir <ArrowRight size={15} />
                </span>
              </div>
            </Link>
            );
          })}
        </div>

        {/* 2 · Autres services — cartes secondaires compactes */}
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {secondary.map(({ to, label, desc, img, isNew }) => (
            <Link
              key={to}
              to={to}
              data-testid={`service-card-${to}`}
              className="group relative flex items-stretch overflow-hidden rounded-2xl border border-slate-200/80 bg-white/90 backdrop-blur-sm transition-all hover:-translate-y-1 hover:border-[#E05638]/40 hover:shadow-[0_12px_30px_-12px_rgba(224,86,56,0.35)]"
            >
              <div className="relative w-28 md:w-36 shrink-0 overflow-hidden">
                <img
                  src={img}
                  alt={label}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="flex flex-col p-4 md:p-5 min-w-0">
                {isNew && (
                  <span className="self-start mb-1.5 text-[9px] font-extrabold uppercase tracking-wider text-white bg-[#E05638] rounded-full px-1.5 py-0.5 leading-none">
                    Nouveau
                  </span>
                )}
                <h3 className="font-bold text-slate-900 text-sm md:text-base leading-tight">{label}</h3>
                <p className="mt-1 text-xs md:text-sm text-slate-500 leading-snug flex-1">{desc}</p>
                <span className="mt-2 inline-flex items-center gap-1 text-xs md:text-sm font-bold text-[#E05638] group-hover:gap-2 transition-all">
                  Découvrir <ArrowRight size={14} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
